// /en/mfa-legalisation/$country — EN per-country detail (Phase P-MFA-LEGALISATION)
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import CountryDeepContent from "@/components/CountryDeepContent";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { MFA_COUNTRIES, MFA_COUNTRY_SLUGS, getMfaCountry } from "@/data/mfa-legalisation-countries";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/mfa-legalisation/$country")({
  beforeLoad: ({ params }) => {
    if (!MFA_COUNTRY_SLUGS.includes(params.country)) throw notFound();
  },
  head: ({ params }) => {
    const c = getMfaCountry(params.country)!;
    const thPath = `/mfa-legalisation/${c.slug}`;
    const enPath = `/en/mfa-legalisation/${c.slug}`;
    const title = `${c.countryEn} Embassy Legalisation + Thai MFA — ${c.totalDays} days | NYC`;
    const desc = `${c.leadEn} · Translation: ${c.translationLang} · End-to-end ${c.totalDays} days. Message us for a same-day answer.`;
    const steps = [
      { name: "Certified / sworn translation", text: `Translate to ${c.translationLang} by an embassy-registered translator.` },
      { name: "Notary Public", text: "Notarial Services Attorney certifies signatures and true copies (same day)." },
      { name: "Thai MFA Chaeng Wattana", text: "Department of Consular Affairs — 2 working days, THB 200/doc standard or THB 400/doc express." },
      { name: `${c.countryEn} Embassy Bangkok`, text: `${c.embassyAddress} — ${c.embassyPhone}. Embassy fee THB ${c.embassyFeeTHB.toLocaleString()}/doc.` },
      { name: "Door-to-door delivery", text: "Free Bangkok courier above THB 5,000; nationwide EMS." },
    ];
    const faqs = [
      { q: `Which documents can be legalised for ${c.countryEn}?`, a: `Most requested: ${c.topDocs.join(", ")} — NYC accepts other documents on request.` },
      { q: `What translation language is required?`, a: c.translationLang },
      { q: `How long does it take?`, a: `${c.totalDays} working days end-to-end once originals are ready — rush available via LINE @nyc-notary.` },
      { q: `What is the all-in price?`, a: `From THB 3,000/doc (including MFA + courier) + ${c.countryEn} embassy fee THB ${c.embassyFeeTHB.toLocaleString()} + translation from THB 800/page.` },
      { q: `Is ${c.countryEn} part of the Hague Apostille Convention?`, a: c.noteEn },
      { q: `Where is the ${c.countryEn} Embassy in Bangkok?`, a: `${c.embassyAddress}, phone ${c.embassyPhone}.` },
    ];
    const breadcrumbs = [
      { name: "Home", path: "/en" },
      { name: "MFA Legalisation", path: "/en/mfa-legalisation" },
      { name: c.countryEn, path: enPath },
    ];
    return {
      meta: [
        { title: ctaTitle(title, "/en/mfa-legalisation/$country", "en") }, { name: "description", content: ctaDesc(desc, "/en/mfa-legalisation/$country", "en")},
        { name: "keywords", content: c.keywordsEn.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/en/mfa-legalisation/$country", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/mfa-legalisation/$country", "en")},
        { property: "og:url", content: `${SITE}${enPath}` }, { property: "og:locale", content: "en_US" },
      ],
      links: canonicalAndHreflang(thPath, enPath),
      scripts: buildLandingSchemas({
        path: enPath, title, description: desc,
        serviceType: `MFA + Embassy Legalisation for ${c.countryEn}`,
        priceFromThb: 3000, steps, faqs, breadcrumbs,
      }),
    };
  },
  component: MfaDetailEn,
});

function MfaDetailEn() {
  const { country } = Route.useParams();
  const c = getMfaCountry(country)!;
  const nearby = MFA_COUNTRIES.filter((x) => x.region === c.region && x.slug !== c.slug).slice(0, 6);
  return (
    <>
      <ServiceLanding
        locale="en"
        badge={`${c.flag} ${c.countryEn} · Non-Apostille`}
        title={`${c.countryEn} Legalisation — Thai MFA + Embassy Bangkok`}
        subtitle={c.leadEn}
        quickAnswer={{
          q: `What is the chain to legalise a Thai document for ${c.countryEn}?`,
          a: `Translation → Notary → Thai MFA Chaeng Wattana → ${c.countryEn} Embassy Bangkok (${c.embassyAddress.split(",")[0]}) — completed in ${c.totalDays} days. From THB 3,000/doc + embassy fee THB ${c.embassyFeeTHB.toLocaleString()}. ${c.noteEn}`,
        }}
        keyFacts={[
          { k: "Region", v: c.region },
          { k: "Translation language", v: c.translationLang },
          { k: "Turnaround", v: `${c.totalDays} working days` },
          { k: "Embassy fee", v: `THB ${c.embassyFeeTHB.toLocaleString()}/doc` },
          { k: "MFA fee", v: "THB 200/doc standard · THB 400/doc express" },
          { k: "NYC bundle from", v: "THB 3,000/doc (all-in)" },
          { k: "Embassy address", v: c.embassyAddress },
          { k: "Embassy phone", v: c.embassyPhone },
        ]}
        heroBullets={[
          `Translation by ${c.countryEn} Embassy-registered translators`,
          `Door-to-door pickup and return in Bangkok`,
          `Milestone updates via LINE @nyc-notary`,
          `99.6% first-time acceptance`,
          c.noteEn,
        ]}
        whoFor={{
          th: [
            `Thai-${c.demonymEn} couples needing marriage/birth certificates accepted in ${c.countryEn}`,
            `Thai engineers/workers relocating to ${c.countryEn} (Police Clearance + degrees + transcripts)`,
            `Thai companies opening branches / JVs in ${c.countryEn} (Board Resolution + corporate certificates)`,
          ],
          foreign: [
            `${c.demonymEn} nationals needing Thai-issued documents accepted by ${c.countryEn} authorities`,
            `HR teams relocating Thai employees to ${c.countryEn}`,
            `Cross-border lawyers preparing POA for ${c.countryEn} courts`,
          ],
        }}
        steps={[
          { name: "Sworn translation", text: `Translate to ${c.translationLang}` },
          { name: "Notary Public", text: "Notarial Services Attorney, same day" },
          { name: "Thai MFA Chaeng Wattana", text: "Department of Consular Affairs, 2 working days" },
          { name: `${c.countryEn} Embassy`, text: c.embassyAddress },
          { name: "Courier return", text: "Free above THB 5,000 in Bangkok; EMS nationwide" },
        ]}
        pricing={[
          { label: "Notary + true-copy certification", price: "1,500/doc", note: "Notarial Services Attorney" },
          { label: "Certified translation", price: "800/page", note: c.translationLang },
          { label: "Thai MFA (2 days standard)", price: "200/doc", note: "Government fee" },
          { label: "Thai MFA (1-day express)", price: "400/doc", note: "Express counter" },
          { label: `${c.countryEn} Embassy fee`, price: `${c.embassyFeeTHB.toLocaleString()}/doc`, note: "Per embassy schedule" },
          { label: "NYC bundle", price: "from 3,000", note: `All-in + courier · ${c.totalDays} days` },
        ]}
        faqs={[
          { q: `Which documents can be legalised for ${c.countryEn}?`, a: `Most requested: ${c.topDocs.join(", ")} — NYC accepts other documents on request.` },
          { q: `What translation language is required?`, a: c.translationLang },
          { q: `How long does it take?`, a: `${c.totalDays} working days end-to-end once originals are ready — rush available via LINE @nyc-notary.` },
          { q: `What is the all-in price?`, a: `From THB 3,000/doc (including MFA + courier) + ${c.countryEn} embassy fee THB ${c.embassyFeeTHB.toLocaleString()} + translation from THB 800/page.` },
          { q: `Is ${c.countryEn} part of the Hague Apostille Convention?`, a: c.noteEn },
          { q: `Where is the ${c.countryEn} Embassy in Bangkok?`, a: `${c.embassyAddress}, phone ${c.embassyPhone}.` },
        ]}
        relatedLinks={[
          { to: "/en/mfa-legalisation", label: "← Back to MFA Legalisation hub" },
          { to: "/en/apostille", label: "Apostille (122 Hague countries)" },
          { to: "/en/translate", label: "Certified Translation" },
          { to: "/en/notary", label: "Notary Public" },
          { to: "/en/translate/mfa", label: "MFA Translation Hub" },
        ]}
      />

      {nearby.length > 0 && (
        <section className="px-4 py-12">
          <div className="mx-auto max-w-5xl">
            <h2 className="text-2xl font-extrabold sm:text-3xl">Nearby destinations in {c.region}</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {nearby.map((n) => (
                <Link key={n.slug} to="/en/mfa-legalisation/$country" params={{ country: n.slug }}
                  className="rounded-xl border border-border bg-card p-4 hover:border-primary"
                  data-track-zone="mfa-detail-nearby-en">
                  <div className="font-bold">{n.flag} {n.countryEn}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{n.totalDays} days · THB {n.embassyFeeTHB.toLocaleString()}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      <CountryDeepContent lang="en" service="mfa" slug={c.slug} countryTh={c.nameTh} countryEn={c.countryEn} flag={c.flag} hague={false} priceFromTh={c.embassyFeeTHB + 2500} turnaround={`${c.totalDays} business days`} />
    </>
  );
}
